import React from 'react';
import { Button } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type HandleErrorApiProps = {
  variant?: 'error' | 'warning';
  errorCode?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  onRetry?: () => void | Promise<void>;
  onBack?: () => void | Promise<void>;
  retryText?: string;
  backText?: string;
  showRetry?: boolean;
  showBack?: boolean;
  retryLoading?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Căn giữa theo viewport (mặc định). Đặt false khi nhúng trong dialog / card. */
  fullHeight?: boolean;
};

const ErrorApiIllustration: React.FC = () => (
  <svg
    width="300"
    height="200"
    viewBox="0 0 300 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <ellipse cx="86" cy="62" rx="48" ry="20" fill="#E7EEF6" />
    <ellipse cx="62" cy="68" rx="26" ry="14" fill="#E7EEF6" />
    <ellipse cx="228" cy="50" rx="44" ry="18" fill="#E3EAF3" />
    <ellipse cx="254" cy="56" rx="20" ry="11" fill="#E3EAF3" />
    <ellipse cx="156" cy="34" rx="18" ry="9" fill="#EEF3F8" />

    <rect x="86" y="42" width="152" height="108" rx="16" fill="#CDD8E6" />
    <rect x="97" y="53" width="130" height="80" rx="8" fill="#F8FAFC" />

    <text
      x="162"
      y="108"
      textAnchor="middle"
      fill="#8B9CB0"
      fontFamily="Roboto, Arial, sans-serif"
      fontSize="42"
      fontWeight="600"
    >
      :(
    </text>

    <rect x="145" y="150" width="34" height="14" rx="3" fill="#C5D0DE" />
    <rect x="116" y="162" width="92" height="11" rx="5.5" fill="#D5DEEA" />

    <circle cx="226" cy="48" r="17" fill="#0C68E9" />
    <path
      d="M226 40.2c1 0 1.75.75 1.75 1.75v8.6c0 1-.75 1.75-1.75 1.75s-1.75-.75-1.75-1.75v-8.6c0-1 .75-1.75 1.75-1.75Zm0 15.6a2.05 2.05 0 1 1 0 4.1 2.05 2.05 0 0 1 0-4.1Z"
      fill="#fff"
    />

    <ellipse cx="54" cy="170" rx="18" ry="6" fill="#E7EEF6" />
    <path d="M42 156h24l-3.5 16H45.5L42 156Z" fill="#E2B48A" />
    <path d="M44.5 156h19l-1.6 7.5H46.1L44.5 156Z" fill="#EBC49A" />
    <ellipse cx="48" cy="130" rx="10" ry="15" fill="#8FCBAA" transform="rotate(-22 48 130)" />
    <ellipse cx="66" cy="126" rx="9" ry="14" fill="#6FB392" transform="rotate(26 66 126)" />
    <ellipse cx="57" cy="140" rx="8" ry="12" fill="#7DB89A" />
  </svg>
);

const HandleErrorApi: React.FC<HandleErrorApiProps> = ({
  title,
  description,
  variant = 'error',
  errorCode,
  onRetry,
  retryText,
  showRetry = false,
  retryLoading = false,
  className,
  style,
  fullHeight = true,
}) => {
  const { t } = useTranslation();

  const handleRetry = () => {
    if (onRetry) {
      void onRetry();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: fullHeight ? 'calc(100vh - 100px)' : undefined,
        padding: fullHeight ? '40px 24px' : '24px 16px',
        textAlign: 'center',
        ...style,
      }}
    >
      {variant === 'warning'
        ? <ExclamationCircleOutlined aria-hidden style={{ fontSize: 40, color: '#d48806' }} />
        : <ErrorApiIllustration />}

      <h2
        style={{
          margin: '28px 0 0',
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.35,
          color: '#1C252E',
        }}
      >
        {title ?? t('bsc.msg.khongthetaidulieu', 'Không thể tải dữ liệu')}
      </h2>

      <p
        style={{
          margin: '10px 0 0',
          maxWidth: 420,
          fontSize: 15,
          lineHeight: 1.65,
          color: '#637381',
          whiteSpace: 'pre-line',
          overflowWrap: 'anywhere',
        }}
      >
        {description ?? (
          <>
            {t('bsc.msg.loi_ket_noi_he_thong', 'Đã xảy ra sự cố khi kết nối với hệ thống.')}
            <br />
            {t(
              'bsc.msg.dulieukhongbianhuong',
              'Dữ liệu của bạn không bị ảnh hưởng. Vui lòng thử lại sau ít phút.',
            )}
          </>
        )}
      </p>
      {errorCode && (
        <p style={{ margin: '16px 0 0', fontSize: 13, color: '#637381', overflowWrap: 'anywhere' }}>
          {t('common.error.code', 'Mã lỗi')}: <code>{errorCode}</code>
        </p>
      )}

      {showRetry && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 28,
          }}
        >
          <Button
            type="primary"
            size="large"
            icon={<ReloadOutlined />}
            loading={retryLoading}
            onClick={handleRetry}
          >
            {retryText ?? t('bsc.btn.thulai', 'Tải lại')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default HandleErrorApi;
